import React, { Component, ReactNode } from 'react';
import { Alert } from '@c2fo/liquidity';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary component for catching and displaying React errors
 * Provides user-friendly error messages and prevents entire app crashes
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDevelopment = process.env.NODE_ENV === 'development';
      const errorMessage =
        isDevelopment && this.state.error?.message
          ? this.state.error.message
          : 'An unexpected error occurred. Please try refreshing the page.';

      return (
        <Alert
          type="error"
          variant="standard"
          full
          title="Something went wrong"
          description={errorMessage}
          action={{
            text: 'Reload Page',
            onClick: () => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            },
          }}
        />
      );
    }

    return this.props.children;
  }
}
